import React, { useState, useEffect } from "react";
import { Drawer, Button, Input, Form, Select, message,DatePicker } from "antd";

const { Option } = Select;

const GenericDrawer = ({
  isVisible,
  handleCancel,
  title,
  inputs, // Array of input fields with attributes (label, name, type, required, options, conditionalFields)
  onSubmit, // Function to handle form submission
  predefinedData = {}, // Optional predefined data for edit forms
  footerButtons = ["Submit", "Cancel"], // Customizable footer buttons
}) => {
  const [form] = Form.useForm(); // Ant Design form instance
  const [fieldDependencies, setFieldDependencies] = useState({});
  const [dynamicInputs, setDynamicInputs] = useState({}); // To handle dynamic input fields

  useEffect(() => {
    if (isVisible) {
      if (predefinedData && Object.keys(predefinedData).length > 0) {
        form.setFieldsValue(predefinedData); // Set predefined values to the form
        setFieldDependencies(predefinedData); // Set initial dependencies
      } else {
        form.resetFields(); // Reset form if no predefined data
        setFieldDependencies({});
      }
    }
  }, [isVisible, predefinedData, form]);

  // Update dependencies on field change
  const handleFieldChange = (changedValues) => {
    setFieldDependencies((prev) => ({ ...prev, ...changedValues }));
  };

  const handleAddDynamicInput = (fieldName) => {
    setDynamicInputs((prev) => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), ""],
    }));
  };

  const handleDynamicInputChange = (fieldName, index, value) => {
    setDynamicInputs((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].map((item, idx) =>
        idx === index ? value : item
      ),
    }));
  };

  const handleRemoveDynamicInput = (fieldName, index) => {
    setDynamicInputs((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, idx) => idx !== index),
    }));
  };


  // Generate form items dynamically based on input props and dependencies
  const renderFormItems = () => {
    return inputs.map((input) => {
      if (input.type === "dynamicInput") {
        const dynamicFieldName = input.name;
        return (
          <Form.Item key={dynamicFieldName} label={input.label}>
            {(dynamicInputs[dynamicFieldName] || []).map((value, index) => (
              <div key={index} style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                <Input
                  value={value}
                  placeholder={input.placeholder || ""}
                  onChange={(e) =>
                    handleDynamicInputChange(dynamicFieldName, index, e.target.value)
                  }
                  style={{ marginRight: 8 }}
                />
                <Button
                  type="text"
                  danger
                  onClick={() => handleRemoveDynamicInput(dynamicFieldName, index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button type="dashed" onClick={() => handleAddDynamicInput(dynamicFieldName)}>
              Add {input.label}
            </Button>
          </Form.Item>
        );
      }

      const rules = input.required
        ? [{ required: true, message: `${input.label} is required` }]
        : [];

      let inputComponent;
      switch (input.type) {
        case "select":
          inputComponent = (
            <Select placeholder={input.placeholder || ""}>
              {input.options?.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          );
          break;
          case "multi-select":
            inputComponent = (
              <Select
              mode="multiple"
              placeholder={input.placeholder || ""}
              defaultValue={predefinedData[input.name] || []} // Ensure agentIds is passed as an array
            >
              {input.options?.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
            );
            break;    
          case "textarea":
          inputComponent = <Input.TextArea placeholder={input.placeholder || ""} autoSize={{minRows:4, maxRows:8}} />;
          break;
        case "date":
          inputComponent = <DatePicker placeholder={input.placeholder || ""} style={{ width: "100%" }} />;
          break;
        default:
          inputComponent = <Input type={input.type} placeholder={input.placeholder || ""} />;
      }

      return (
        <Form.Item key={input.name} label={input.label} name={input.name} rules={rules} extra={input.helperText ? <span style={{ fontSize: "12px", color: "#888" }}>{input.helperText}</span> : null}>
          {inputComponent}
        </Form.Item>
      );
    });
  };

  // Handle form submission
  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        onSubmit(values); // Call the onSubmit function with form values
      })
      .catch((error) => {
        message.error("Please fill out the required fields.");
        console.log(error)
      });
  };

  return (
    <Drawer
      title={title}
      visible={isVisible}
      onClose={handleCancel}
      width={480} // Adjust the width as needed
      footer={null} // Disable default footer
    >
      <Form form={form} layout="vertical" onValuesChange={handleFieldChange}>
        {renderFormItems()}
      </Form>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
        {footerButtons.includes("Submit") && (
          <Button type="primary" onClick={handleSubmit} style={{ marginRight: 8 }}>
            Submit
          </Button>
        )}
        {footerButtons.includes("Cancel") && (
          <Button onClick={handleCancel}>Cancel</Button>
        )}
      </div>
    </Drawer>
  );
};

export default GenericDrawer;
